import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { saveAs } from "file-saver";

import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import Formulario from "../../../Componentes/Utils/Formulario";
import FormComp from "../../../Componentes/FormComp";
import SmallLoading from "../../../Componentes/Utils/SmallLoading";
import MultiFileUploader from "../../../Componentes/Utils/MultiFileUploader";
import ConfirmDeleteAnexoPC from "./ConfirmDeleteAnexoPC";
import useApi from "../../../hooks/useApi";
import { formatBRLFromCents, parseCentsFromInput } from "../../../Funcoes/Money";

import { FiDownload } from "react-icons/fi";
import { GoTrash } from "react-icons/go";
import {
    AiOutlineFilePdf,
    AiOutlineFileImage,
    AiOutlineFileWord,
    AiOutlineFileExcel,
    AiOutlineFileUnknown,
    AiOutlineLoading,
} from "react-icons/ai";

const Titulo = styled.h2`
    margin: 0 0 12px 3px;
    font-size: 18px;
    color: #444;
    text-align: left;
`

const CheckboxContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin: 20px 0;

    h3 {
        margin: 0;
        font-size: 16px;
        color: #555;
        text-align: center;
    }
`
const CheckboxWrapper = styled.div`
    margin: auto;
    display: flex;
    align-items: center;
    gap: 20px;
    color: #333;
    label {
        display: flex;
        align-items: center;
        gap: 5px;
        font-size: 14px;
        input {
            width: 16px;
            height: 16px;
        }
    }
`

const AnexosSecaoHeader = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    margin: 18px 0 8px 0;
    gap: 12px;
`
const AnexosTitulo = styled.h3`
    margin: 0 0 0 3px;
    font-size: 15px;
    color: #444;
    text-align: left;
    font-weight: 600;
`
const AnexosContainer = styled.div`
    width: 100%;
    max-height: 220px;
    border: 1px solid rgba(0, 0, 0, 0.22);
    border-radius: 8px;
    box-sizing: border-box;
    overflow-y: auto;
    background: #fafafa;
`
const AnexosLista = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`
const AnexoLinha = styled.li`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 13px;
    color: #333;
    &:last-child {
        border-bottom: none;
    }
    &:nth-child(even) {
        background-color: #fff;
    }
`
const AnexoIconeTipo = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 36px;
`
const AnexoTextoCol = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`
const AnexoNome = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    font-size: 13px;
    color: #1e293b;
`
const AnexoTamanho = styled.span`
    font-size: 12px;
    color: #64748b;
`
const AnexoAcoes = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
`
const IconBtn = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    color: #475569;
    transition: background 0.15s ease, color 0.15s ease;
    &:hover {
        background: rgba(0, 0, 0, 0.06);
        color: #0f172a;
    }
    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`
const IconDownload = styled(FiDownload)`
    width: 18px;
    height: 18px;
`
const IconDownloadLoading = styled(AiOutlineLoading)`
    width: 18px;
    height: 18px;
    color: #2563eb;
    animation: pcEditAnexoSpin 0.85s linear infinite;
    @keyframes pcEditAnexoSpin {
        to {
            transform: rotate(360deg);
        }
    }
`
const IconDelete = styled(GoTrash)`
    width: 18px;
    height: 18px;
    color: #e11d48;
`
const AnexosVazio = styled.p`
    margin: 0;
    padding: 14px 12px;
    font-size: 13px;
    color: #64748b;
    text-align: center;
`

const TIPOS_CONTRATO = ["PRINCIPAL", "SFE", "FABRICAÇÃO", "CLIENTE ADICIONAL"];

function formatarTamanhoArquivo(bytes) {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n < 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(filename) {
    const name = String(filename || "").toLowerCase();
    if (
        name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg")
        || name.endsWith(".gif") || name.endsWith(".webp") || name.endsWith(".bmp")
    ) {
        return <AiOutlineFileImage size={24} color="#4a90e2" />;
    }
    if (name.endsWith(".pdf")) return <AiOutlineFilePdf size={24} color="#e63946" />;
    if (name.endsWith(".doc") || name.endsWith(".docx")) {
        return <AiOutlineFileWord size={24} color="#2a6db0" />;
    }
    if (name.endsWith(".xls") || name.endsWith(".xlsx")) {
        return <AiOutlineFileExcel size={24} color="#207245" />;
    }
    return <AiOutlineFileUnknown size={24} color="#64748b" />;
}

function nomeArquivoAnexo(anexo) {
    return anexo.original_nome || anexo.serv_nome || "Arquivo";
}

//Backend retorna data em "YYYY-MM-DD" ou ISO; <input type="date"> precisa de "YYYY-MM-DD".
function formatDataParaInput(data) {
    if (!data) return "";
    const s = String(data);
    return s.length >= 10 ? s.slice(0, 10) : s;
}

const initialFormState = {
    grupo: "",
    numPedido: "",
    valorFaturamento: 0,
    valorFaturamentoDireto: 0,
    tipoContrato: "",
    nomeProjeto: "",
    pasp: "",
    descricao: "",
    contratante: "",
    observacoes: "",
    numContrato: "",
    dataContrato: "",
    art: "",
    cno: "",
    pc_pendente: false,
    proposta_pendente: false,
    contrato_pendente: false,
};

const ModalEditItem = ({ id, fetchData, fecharModal }) => {

    const api = useApi();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [pedido, setPedido] = useState(null);
    const [formValues, setFormValues] = useState(initialFormState);

    const [grupos, setGrupos] = useState([]);
    const [loadingGrupos, setLoadingGrupos] = useState(false);

    const [anexosExistentes, setAnexosExistentes] = useState([]);
    const [arquivos, setArquivos] = useState([]);

    const [downloadingIds, setDownloadingIds] = useState([]);
    const [anexoParaExcluir, setAnexoParaExcluir] = useState(null);

    const cancelledRef = useRef(false);

    useEffect(() => {

        cancelledRef.current = false;

        const carregarPedido = async () => {

            if (id == null) {
                setLoading(false);
                return;
            }

            try {

                setLoading(true);

                const { data } = await api.get(`/Painel/API/ControleMedicao/GetPedido/index.php?id=${id}`);

                if (cancelledRef.current) return;

                setPedido(data);
                setAnexosExistentes(Array.isArray(data?.anexos) ? data.anexos : []);

                setFormValues({
                    grupo: data?.grupo != null ? String(data.grupo) : "",
                    numPedido: data?.num_pedido ?? "",
                    valorFaturamento: Number(data?.valor_faturamento) || 0,
                    valorFaturamentoDireto: Number(data?.valor_faturamento_direto) || 0,
                    tipoContrato: data?.tipo_contrato ?? "",
                    nomeProjeto: data?.nome_projeto ?? "",
                    pasp: data?.pasp ?? "",
                    descricao: data?.descricao ?? "",
                    contratante: data?.contratante ?? "",
                    observacoes: data?.observacoes ?? "",
                    numContrato: data?.num_contrato ?? "",
                    dataContrato: formatDataParaInput(data?.data_contrato),
                    art: data?.art ?? "",
                    cno: data?.cno ?? "",
                    pc_pendente: !!Number(data?.pc_pendente),
                    proposta_pendente: !!Number(data?.prop_pendente),
                    contrato_pendente: !!Number(data?.contrato_pendente),
                });

                //Carrega grupos da obra do pedido para popular o select.
                if (data?.id_obra) {
                    try {
                        setLoadingGrupos(true);
                        const { data: gruposData } = await api.get(
                            `/Painel/API/ControleMedicao/ListGrupos/index.php?obra=${data.id_obra}`
                        );
                        if (!cancelledRef.current) {
                            setGrupos(Array.isArray(gruposData) ? gruposData : []);
                        }
                    } catch (err) {
                        console.error(err);
                        if (!cancelledRef.current) setGrupos([]);
                    } finally {
                        if (!cancelledRef.current) setLoadingGrupos(false);
                    }
                }

            } catch (err) {

                console.error("Erro ao carregar pedido de compra:", err);

            } finally {

                if (!cancelledRef.current) setLoading(false);

            }

        };

        carregarPedido();

        return () => { cancelledRef.current = true; };

    // eslint-disable-next-line react-hooks/exhaustive-deps -- useApi gera nova instância a cada render
    }, [id]);

    const handleChange = (e) => {

        const { name, value } = e.target;

        setFormValues((prev) => {
            if (name === "valorFaturamento" || name === "valorFaturamentoDireto") {
                return { ...prev, [name]: parseCentsFromInput(value) };
            }
            if (name === "grupo" || name === "dataContrato") {
                return { ...prev, [name]: value };
            }
            return { ...prev, [name]: typeof value === "string" ? value.toUpperCase() : value };
        });

    };

    const handleDownloadAnexo = async (anexo) => {

        if (anexo?.id == null) return;

        const aid = anexo.id;
        setDownloadingIds((prev) => (prev.includes(aid) ? prev : [...prev, aid]));

        try {

            const { data } = await api.get(
                `/Painel/API/ControleMedicao/DownloadPCAnexo/index.php?id=${aid}`,
                { responseType: "blob" }
            );
            saveAs(data, nomeArquivoAnexo(anexo));

        } catch (e) {

            console.error(e);

        } finally {

            setDownloadingIds((prev) => prev.filter((x) => x !== aid));

        }

    };

    const handleSubmit = async (e) => {

        e.preventDefault();

        try {

            setSaving(true);

            const formData = new FormData();
            formData.append("id", String(id));

            Object.entries(formValues).forEach(([key, value]) => {
                formData.append(key, value);
            });

            arquivos.forEach((f) => {
                formData.append("files[]", f.file);
            });

            await api.post("/Painel/API/ControleMedicao/EditPedido/", formData);

            if (typeof fetchData === "function") fetchData();
            fecharModal();

        } catch (err) {

            console.error("Erro ao salvar pedido de compra:", err);

        } finally {

            setSaving(false);

        }

    };

    const valorTotalCents = (formValues.valorFaturamento || 0) + (formValues.valorFaturamentoDireto || 0);

    return (
        <>
            <Overlay state={true}>
                <Modal header close={fecharModal}>
                    {loading ? (
                        <SmallLoading />
                    ) : (
                        <>
                            <Titulo>EDITAR PEDIDO DE COMPRA</Titulo>
                            <Formulario onSubmit={handleSubmit}>
                                <FormComp
                                    title="OBRA"
                                    type="text"
                                    readOnly
                                    valor={pedido?.obra || ""}
                                />
                                <FormComp
                                    title="GRUPO"
                                    type="select"
                                    obj
                                    options={grupos.map((g) => ({
                                        value: g.id,
                                        label: `${g.number} - ${g.descricao}`,
                                    }))}
                                    valor={formValues.grupo}
                                    name="grupo"
                                    changeFunc={handleChange}
                                    required
                                    disabled={loadingGrupos}
                                />
                                <FormComp
                                    title="Nº PEDIDO DE COMPRA"
                                    type="text"
                                    valor={formValues.numPedido}
                                    name="numPedido"
                                    changeFunc={handleChange}
                                    required
                                />
                                <FormComp
                                    title="VALOR FATURAMENTO"
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="R$ 0,00"
                                    valor={formatBRLFromCents(formValues.valorFaturamento)}
                                    name="valorFaturamento"
                                    changeFunc={handleChange}
                                    required
                                />
                                <FormComp
                                    title="VALOR FATURAMENTO DIRETO"
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="R$ 0,00"
                                    valor={formatBRLFromCents(formValues.valorFaturamentoDireto)}
                                    name="valorFaturamentoDireto"
                                    changeFunc={handleChange}
                                />
                                <FormComp
                                    title="VALOR TOTAL"
                                    type="text"
                                    valor={formatBRLFromCents(valorTotalCents)}
                                    readOnly
                                />
                                <FormComp
                                    title="TIPO DO CONTRATO"
                                    type="select"
                                    options={TIPOS_CONTRATO}
                                    valor={formValues.tipoContrato}
                                    name="tipoContrato"
                                    changeFunc={handleChange}
                                    required
                                />
                                <FormComp
                                    title="NOME DO PROJETO"
                                    type="text"
                                    valor={formValues.nomeProjeto}
                                    name="nomeProjeto"
                                    changeFunc={handleChange}
                                    required
                                />
                                <FormComp
                                    title="PASP"
                                    type="text"
                                    valor={formValues.pasp}
                                    name="pasp"
                                    changeFunc={handleChange}
                                    required
                                />
                                <FormComp
                                    title="DESCRIÇÃO"
                                    type="text"
                                    valor={formValues.descricao}
                                    name="descricao"
                                    changeFunc={handleChange}
                                    required
                                />
                                <FormComp
                                    title="CONTRATANTE"
                                    type="text"
                                    valor={formValues.contratante}
                                    name="contratante"
                                    changeFunc={handleChange}
                                    required
                                />
                                <FormComp
                                    title="OBSERVAÇÕES"
                                    type="textarea"
                                    valor={formValues.observacoes}
                                    name="observacoes"
                                    changeFunc={handleChange}
                                />
                                <FormComp
                                    title="Nº DO CONTRATO"
                                    type="text"
                                    valor={formValues.numContrato}
                                    name="numContrato"
                                    changeFunc={handleChange}
                                />
                                <FormComp
                                    title="DATA DO CONTRATO"
                                    type="date"
                                    valor={formValues.dataContrato}
                                    name="dataContrato"
                                    changeFunc={handleChange}
                                    required
                                />
                                <FormComp
                                    title="ART"
                                    type="text"
                                    valor={formValues.art}
                                    name="art"
                                    changeFunc={handleChange}
                                />
                                <FormComp
                                    title="CNO"
                                    type="text"
                                    valor={formValues.cno}
                                    name="cno"
                                    changeFunc={handleChange}
                                />

                                <CheckboxContainer>
                                    <h3>ARQUIVO PENDENTE</h3>
                                    <CheckboxWrapper>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={formValues.pc_pendente}
                                                onChange={(e) =>
                                                    setFormValues((prev) => ({
                                                        ...prev,
                                                        pc_pendente: e.target.checked,
                                                    }))
                                                }
                                            />
                                            PEDIDO DE COMPRA
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={formValues.proposta_pendente}
                                                onChange={(e) =>
                                                    setFormValues((prev) => ({
                                                        ...prev,
                                                        proposta_pendente: e.target.checked,
                                                    }))
                                                }
                                            />
                                            PROPOSTA
                                        </label>
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={formValues.contrato_pendente}
                                                onChange={(e) =>
                                                    setFormValues((prev) => ({
                                                        ...prev,
                                                        contrato_pendente: e.target.checked,
                                                    }))
                                                }
                                            />
                                            CONTRATO
                                        </label>
                                    </CheckboxWrapper>
                                </CheckboxContainer>

                                <AnexosSecaoHeader>
                                    <AnexosTitulo>ANEXOS EXISTENTES</AnexosTitulo>
                                </AnexosSecaoHeader>
                                <AnexosContainer>
                                    {anexosExistentes.length === 0 ? (
                                        <AnexosVazio>Nenhum anexo cadastrado.</AnexosVazio>
                                    ) : (
                                        <AnexosLista>
                                            {anexosExistentes.map((anexo) => {
                                                const nome = nomeArquivoAnexo(anexo);
                                                const baixando =
                                                    anexo.id != null && downloadingIds.includes(anexo.id);
                                                return (
                                                    <AnexoLinha
                                                        key={
                                                            anexo.id ??
                                                            `${anexo.serv_nome}-${anexo.original_nome}`
                                                        }
                                                    >
                                                        <AnexoIconeTipo title={nome}>
                                                            {getFileIcon(nome)}
                                                        </AnexoIconeTipo>
                                                        <AnexoTextoCol>
                                                            <AnexoNome title={nome}>{nome}</AnexoNome>
                                                            <AnexoTamanho>
                                                                {formatarTamanhoArquivo(anexo.tamanho)}
                                                            </AnexoTamanho>
                                                        </AnexoTextoCol>
                                                        <AnexoAcoes>
                                                            <IconBtn
                                                                type="button"
                                                                title="Baixar arquivo"
                                                                aria-label="Download"
                                                                disabled={baixando}
                                                                onClick={() => handleDownloadAnexo(anexo)}
                                                            >
                                                                {baixando ? (
                                                                    <IconDownloadLoading aria-hidden />
                                                                ) : (
                                                                    <IconDownload />
                                                                )}
                                                            </IconBtn>
                                                            <IconBtn
                                                                type="button"
                                                                title="Excluir arquivo"
                                                                aria-label="Excluir anexo"
                                                                disabled={anexo.id == null}
                                                                onClick={() =>
                                                                    anexo.id != null &&
                                                                    setAnexoParaExcluir(anexo)
                                                                }
                                                            >
                                                                <IconDelete />
                                                            </IconBtn>
                                                        </AnexoAcoes>
                                                    </AnexoLinha>
                                                );
                                            })}
                                        </AnexosLista>
                                    )}
                                </AnexosContainer>

                                <AnexosSecaoHeader>
                                    <AnexosTitulo>ADICIONAR NOVOS ANEXOS</AnexosTitulo>
                                </AnexosSecaoHeader>
                                <MultiFileUploader files={arquivos} setFiles={setArquivos} />

                                <FormComp
                                    type="submit"
                                    valor={saving ? "SALVANDO..." : "SALVAR"}
                                    disabled={saving}
                                />
                            </Formulario>
                        </>
                    )}
                </Modal>
            </Overlay>
            {anexoParaExcluir && (
                <ConfirmDeleteAnexoPC
                    id={anexoParaExcluir.id}
                    isPedido={true}
                    fecharModal={() => setAnexoParaExcluir(null)}
                    onDelete={() => {
                        const aid = anexoParaExcluir.id;
                        setAnexosExistentes((prev) => prev.filter((a) => a.id !== aid));
                    }}
                />
            )}
        </>
    );

};

export default ModalEditItem;
